import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BsX } from 'react-icons/bs';
import { DECK_HOLDER_PRESETS } from '../config/presentationPresets';
import { backgroundStyle, darkness } from '../utils/presentation';
import './ClassDeckRecipesModule.css';

const PREVIEW_WIDTH = 286;
const PREVIEW_MARGIN = 14;

const cardImage = (card) => Number(card?.type) === 1
  ? `https://art.hearthstonejson.com/v1/render/latest/enUS/256x/${card.cardid}.png`
  : `https://www.hearthcards.net/cards/${card?.cardid}.webp`;

function useFinePointer() {
  const query = '(hover: hover) and (pointer: fine)';
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return matches;
}

function desktopPreviewPosition(row, isIngame) {
  if (!row) return { left: PREVIEW_MARGIN, top: PREVIEW_MARGIN };
  const rect = row.getBoundingClientRect();
  const height = isIngame ? 433 : 400;
  const roomOnRight = window.innerWidth - rect.right;
  let left = roomOnRight >= PREVIEW_WIDTH + PREVIEW_MARGIN
    ? rect.right + PREVIEW_MARGIN
    : rect.left - PREVIEW_WIDTH - PREVIEW_MARGIN;
  if (left < PREVIEW_MARGIN) left = Math.min(window.innerWidth - PREVIEW_WIDTH - PREVIEW_MARGIN, rect.left + 24);
  const top = Math.max(PREVIEW_MARGIN, Math.min(rect.top + (rect.height / 2) - (height / 2), window.innerHeight - height - PREVIEW_MARGIN));
  return { left: Math.round(left), top: Math.round(top) };
}

function DesktopCardPreview({ card, anchor }) {
  const isIngame = Number(card?.type) === 1;
  const [position, setPosition] = useState(() => desktopPreviewPosition(anchor, isIngame));
  useEffect(() => {
    const update = () => setPosition(desktopPreviewPosition(anchor, isIngame));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor, isIngame]);
  if (!document?.body) return null;
  return createPortal(<div className={`set-deck-card-preview ${isIngame ? 'set-is-ingame' : ''}`} style={position} aria-hidden="true"><img src={cardImage(card)} alt="" /></div>, document.body);
}

function DeckCard({ card, finePointer, onMobilePreview }) {
  const rowRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const quantity = String(card?.quantity) === '2' ? '2' : '';
  return (
    <button
      ref={rowRef}
      type="button"
      className="set-deck-card set-deck-card-row"
      onPointerEnter={() => finePointer && setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => finePointer && setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={() => { if (!finePointer) onMobilePreview(card); }}
      aria-label={`${card?.cardname || 'Card'}${quantity ? ', two copies' : ''}`}
    >
      {finePointer && hovered && <DesktopCardPreview card={card} anchor={rowRef.current} />}
      <span className="set-deck-card-cost set-outlined-text">{card?.cost ?? 0}</span>
      <span className="set-deck-card-name set-outlined-text" title={card?.cardname}>{card?.cardname || 'Untitled card'}</span>
      <span className="set-deck-card-background" />
      <span className={`set-deck-card-counter set-outlined-text ${card?.rarity === 'legendary' ? 'set-is-legendary' : ''}`}>{quantity}</span>
    </button>
  );
}

function MobileCardPreview({ card, onClose }) {
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  if (!card || !document?.body) return null;
  return createPortal(
    <div className="set-deck-mobile-preview" role="presentation" onClick={onClose}>
      <div className="set-deck-mobile-preview-dialog" role="dialog" aria-modal="true" aria-label={card.cardname || 'Card preview'} onClick={(event) => event.stopPropagation()}>
        <button type="button" className="set-deck-mobile-preview-close" onClick={onClose} aria-label="Close card preview"><BsX /></button>
        <img src={cardImage(card)} alt={card.cardname || 'Card preview'} />
      </div>
    </div>,
    document.body,
  );
}

function Deck({ deck, deckIndex, finePointer, onMobilePreview }) {
  const data = deck?.data || {};
  const cards = useMemo(() => [...(data.cards || [])].sort((a, b) => Number(a.cost || 0) - Number(b.cost || 0)), [data.cards]);
  return (
    <article className="set-deck-recipe">
      <div className="set-deck-holder-top"><h3 className="set-deck-holder-title" title={data.title}>{data.title || `Deck ${deckIndex + 1}`}</h3></div>
      <div className="set-deck-holder-middle"><div className="set-deck-card-list">
        {cards.length ? cards.map((card, cardIndex) => <DeckCard key={`${card.cardid || 'card'}-${cardIndex}`} card={card} finePointer={finePointer} onMobilePreview={onMobilePreview} />) : <p className="set-deck-empty">Add cards to this recipe.</p>}
      </div></div>
      <div className="set-deck-holder-bottom" />
    </article>
  );
}

export default function ClassDeckRecipesModule({ module }) {
  const data = module?.data || {};
  const features = module?.features || [];
  const finePointer = useFinePointer();
  const [mobilePreview, setMobilePreview] = useState(null);
  const useImage = data.backgroundMode !== 'color';
  const holder = DECK_HOLDER_PRESETS[data.deckHolderStyle] || DECK_HOLDER_PRESETS.defaultMetal;
  const cardsTopOffset = Number(holder.cardsTopOffset);
  const style = {
    ...backgroundStyle(useImage ? data.selectablebg : '', { size: data.backgroundSize || 'cover', position: data.backgroundPosition || 'center', tileSize: data.backgroundTileSize }),
    backgroundColor: data.backgroundColor || '#20170f',
    '--section-overlay': useImage ? darkness(data.backgroundDarkness, 30) : 0,
    '--deck-holder-top': `url('${holder.top}')`,
    '--deck-holder-middle': `url('${holder.middle}')`,
    '--deck-holder-bottom': `url('${holder.bottom}')`,
    '--deck-holder-width': `${holder.width}px`,
    '--deck-holder-top-height': `${holder.topHeight}px`,
    '--deck-holder-bottom-height': `${holder.bottomHeight}px`,
    '--deck-holder-title-offset': `${holder.titleOffset}px`,
    '--deck-holder-content-inset': `${holder.contentInset}px`,
    '--deck-holder-cards-top-offset': `${Number.isFinite(cardsTopOffset) ? cardsTopOffset : 0}px`,
  };
  return (
    <section className="set-presentation-section set-deck-recipes" style={style}>
      <div className="set-presentation-shade" />
      <div className="set-deck-edge-fade" aria-hidden="true" />
      <div className="container-fluid set-deck-recipes-inner">
        {(data.title || data.intro) && <header className="set-section-heading text-center">{data.title && <h2>{data.title}</h2>}{data.intro && <p>{data.intro}</p>}</header>}
        <div className="set-deck-recipes-grid">{features.map((deck, index) => <Deck key={deck.id || index} deck={deck} deckIndex={index} finePointer={finePointer} onMobilePreview={setMobilePreview} />)}</div>
        {!features.length && <p className="set-deck-module-empty">Add a deck recipe to start building this section.</p>}
      </div>
      {mobilePreview && <MobileCardPreview card={mobilePreview} onClose={() => setMobilePreview(null)} />}
    </section>
  );
}
